import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Alert,
  Link,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Link as LinkIcon,
  Slideshow as SlideshowIcon,
  VideoLibrary as VideoIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import {
  getCourses,
  createCourse,
  deleteCourse,
  getCourseCompletions,
  createCourseCompletion,
  deleteCourseCompletion,
  getEmployees,
  BASE_URL,
} from '../services/api';

const typeIcon = (type) => {
  switch (type) {
    case 'link':
      return <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    case 'video':
      return <VideoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    case 'slides':
      return <SlideshowIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />;
    default:
      return null;
  }
};

const typeLabel = (type) => {
  switch (type) {
    case 'link':
      return 'Enlace / YouTube';
    case 'video':
      return 'Video (archivo)';
    case 'slides':
      return 'Presentación';
    default:
      return type;
  }
};

const parseDate = (value) => {
  try {
    if (!value) return new Date();
    return typeof value === 'string' ? parseISO(value) : new Date(value);
  } catch {
    return new Date();
  }
};

const isHttpUrl = (str) => /^https?:\/\//i.test(String(str || '').trim());

const fileUrl = (filePath) => (filePath ? `${BASE_URL}/${filePath}` : null);

/** Enlace externo o archivo del curso, siempre en pestaña nueva */
const CourseResourceLinks = ({ course }) => {
  const resource = (course.resource || '').trim();
  const uploadedUrl = fileUrl(course.filePath);
  const externalUrl = isHttpUrl(resource) ? resource : null;

  const openLinkProps = {
    target: '_blank',
    rel: 'noopener noreferrer',
    underline: 'hover',
  };

  const linkLabel = () => {
    if (course.type === 'video') return 'Ver video';
    if (course.type === 'slides') return 'Abrir presentación';
    return 'Abrir enlace';
  };

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {externalUrl && (
        <Link href={externalUrl} {...openLinkProps} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <OpenInNewIcon sx={{ fontSize: 16 }} />
          {linkLabel()}
        </Link>
      )}
      {uploadedUrl && (
        <Link href={uploadedUrl} {...openLinkProps} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <OpenInNewIcon sx={{ fontSize: 16 }} />
          {course.type === 'video' ? 'Ver video (archivo)' : course.type === 'slides' ? 'Abrir archivo' : 'Abrir / descargar archivo'}
        </Link>
      )}
      {resource && !externalUrl && !uploadedUrl && (
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {resource}
        </Typography>
      )}
      {externalUrl && resource !== externalUrl && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {resource}
        </Typography>
      )}
    </Stack>
  );
};

const TrainingCourses = () => {
  const [tab, setTab] = useState(0);
  const [courses, setCourses] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [materialFile, setMaterialFile] = useState(null);
  const [form, setForm] = useState({
    title: '',
    type: 'link',
    resource: '',
  });
  const [markOpen, setMarkOpen] = useState(false);
  const [markForm, setMarkForm] = useState({ courseId: '', employeeId: '' });

  const loadCourses = useCallback(async () => {
    try {
      const { data } = await getCourses();
      setCourses(data);
    } catch (error) {
      console.error('Error al cargar cursos:', error);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  }, []);

  const loadCompletionsFlat = useCallback(async () => {
    try {
      const { data: courseList } = await getCourses();
      const lists = await Promise.all(
        courseList.map((c) => getCourseCompletions(c._id).then((r) => r.data))
      );
      const flat = [];
      courseList.forEach((c, i) => {
        (lists[i] || []).forEach((row) => {
          flat.push({
            ...row,
            courseTitle: c.title,
            courseId: c._id,
          });
        });
      });
      flat.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
      setCompletions(flat);
    } catch (error) {
      console.error('Error al cargar avances:', error);
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      setLoading(true);
      await Promise.all([loadCourses(), loadEmployees()]);
      setLoading(false);
    };
    boot();
  }, [loadCourses, loadEmployees]);

  useEffect(() => {
    if (tab === 1) {
      loadCompletionsFlat();
    }
  }, [tab, loadCompletionsFlat]);

  const courseById = useMemo(() => {
    const map = {};
    courses.forEach((c) => {
      map[c._id] = c;
    });
    return map;
  }, [courses]);

  const handleAddCourse = async () => {
    if (!form.title.trim()) {
      alert('El título es obligatorio');
      return;
    }
    if (!materialFile && !form.resource.trim()) {
      alert('Indica una URL/recurso o adjunta un archivo');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('type', form.type);
      fd.append('resource', form.resource.trim());
      if (materialFile) {
        fd.append('material', materialFile);
      }
      await createCourse(fd);
      setForm({ title: '', type: 'link', resource: '' });
      setMaterialFile(null);
      setOpenDialog(false);
      await loadCourses();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Error al crear curso');
    }
  };

  const handleMarkDone = async () => {
    if (!markForm.courseId || !markForm.employeeId) return;
    try {
      await createCourseCompletion(markForm.courseId, markForm.employeeId);
      setMarkForm({ courseId: '', employeeId: '' });
      setMarkOpen(false);
      await loadCompletionsFlat();
      await loadCourses();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Error al registrar avance');
    }
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('¿Eliminar este curso y sus registros de avance?')) return;
    try {
      await deleteCourse(id);
      await loadCourses();
      await loadCompletionsFlat();
    } catch (error) {
      console.error(error);
      alert('Error al eliminar curso');
    }
  };

  const handleDeleteCompletion = async (courseId, completionId) => {
    if (!window.confirm('¿Quitar este registro de curso tomado?')) return;
    try {
      await deleteCourseCompletion(courseId, completionId);
      await loadCompletionsFlat();
    } catch (error) {
      console.error(error);
      alert('Error al eliminar registro');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Capacitación y cursos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setMarkOpen(true)}>
            Registrar avance
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Agregar curso
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Catálogo de cursos" />
          <Tab label="Seguimiento por persona" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Curso</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Recurso / archivo</TableCell>
                <TableCell>Alta</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>Cargando…</TableCell>
                </TableRow>
              ) : courses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No hay cursos. Crea uno con el botón superior.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                courses.map((c) => (
                  <TableRow key={c._id}>
                    <TableCell>{c.title}</TableCell>
                    <TableCell>
                      {typeIcon(c.type)}
                      {typeLabel(c.type)}
                    </TableCell>
                    <TableCell>
                      <CourseResourceLinks course={c} />
                    </TableCell>
                    <TableCell>{format(parseDate(c.createdAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDeleteCourse(c._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empleado</TableCell>
                <TableCell>Curso</TableCell>
                <TableCell>Fecha de registro</TableCell>
                <TableCell>Registrado por</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {completions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      Sin registros. Usa «Registrar avance» para vincular empleados con cursos.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                completions.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>
                      {row.employee
                        ? `${row.employee.firstName} ${row.employee.lastName}`
                        : '—'}
                    </TableCell>
                    <TableCell>{row.courseTitle || courseById[row.course]?.title}</TableCell>
                    <TableCell>{format(parseDate(row.completedAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell>{row.recordedBy?.name || '—'}</TableCell>
                    <TableCell align="right">
                        <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDeleteCompletion(
                            typeof row.course === 'object' && row.course !== null
                              ? row.course._id || row.course
                              : row.course,
                            row._id
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo curso</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Título"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de contenido</InputLabel>
            <Select
              label="Tipo de contenido"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <MenuItem value="link">Enlace / YouTube / página web</MenuItem>
              <MenuItem value="video">Video (archivo subido)</MenuItem>
              <MenuItem value="slides">Presentación (PPT/PDF)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="URL o texto del recurso"
            placeholder="https://…"
            value={form.resource}
            onChange={(e) => setForm({ ...form, resource: e.target.value })}
            helperText="Si adjuntas archivo puedes dejar esto vacío para usar el nombre del archivo."
          />
          <Button variant="outlined" component="label" sx={{ mt: 1 }}>
            Adjuntar archivo (opcional)
            <input
              type="file"
              hidden
              accept=".pdf,.ppt,.pptx,.mp4,.webm,.mov,.mpeg"
              onChange={(e) => setMaterialFile(e.target.files?.[0] || null)}
            />
          </Button>
          {materialFile && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Seleccionado: {materialFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cerrar</Button>
          <Button onClick={handleAddCourse} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={markOpen} onClose={() => setMarkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Registrar curso tomado</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Curso</InputLabel>
            <Select
              label="Curso"
              value={markForm.courseId}
              onChange={(e) => setMarkForm({ ...markForm, courseId: e.target.value })}
            >
              {courses.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Empleado</InputLabel>
            <Select
              label="Empleado"
              value={markForm.employeeId}
              onChange={(e) => setMarkForm({ ...markForm, employeeId: e.target.value })}
            >
              {employees.map((emp) => (
                <MenuItem key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName}
                  {emp.employeeNumber ? ` (${emp.employeeNumber})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkOpen(false)}>Cancelar</Button>
          <Button onClick={handleMarkDone} variant="contained">
            Registrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainingCourses;
